import * as React from 'react';
import {
  Box,
  Icon,
  ParagraphText,
  StackLayout,
} from '@leagueplatform/genesis-core';
import {
  ComplexObjectArrayControlItem,
  NumberIcon,
} from '@web-config-app/entity-form';
import {
  ACTION,
  DECISION,
  DECISION_RESULT_LABELS,
  FlowNode,
  NodeType,
  NodeNextKeyMap,
  START,
  isDecisionNextArray,
  typeIcons,
  isActionNextArray,
} from '@web-config-app/flow-builder';
import { useIntl } from '@leagueplatform/locales';
import { AngleNextIcon } from '@web-config-app/core-react-ui';
import { Nullable } from '@web-config-app/core';

export interface FlowTransitionsProps {
  itemData: FlowNode;
  data: ComplexObjectArrayControlItem[];
  nodeNextKeyMap: NodeNextKeyMap;
}

interface TransitionItemProps {
  targetNode?: ComplexObjectArrayControlItem;
  targetNodeNumber?: number;
  label?: string;
  transitionKey?: string;
  itemLabel?: string;
}

const isNodeType = (value: unknown): value is NodeType =>
  value === 'action' ||
  value === 'decision' ||
  value === 'end' ||
  value === 'start';

const TransitionItem: React.FC<TransitionItemProps> = ({
  targetNode,
  targetNodeNumber,
  label,
  transitionKey,
  itemLabel,
}) => {
  const itemNodeType = targetNode?.itemData?.nodeType;
  const itemNodeTypeIcon = isNodeType(itemNodeType)
    ? typeIcons[itemNodeType]
    : undefined;

  return (
    <StackLayout
      as="li"
      key={transitionKey}
      orientation="horizontal"
      verticalAlignment="center"
      spacing="$half"
      css={{ paddingInlineStart: '$one' }}
    >
      <StackLayout
        orientation="horizontal"
        spacing="$half"
        verticalAlignment="center"
        css={{ flexGrow: 1 }}
      >
        <Icon
          icon={<AngleNextIcon />}
          size="$one"
          tint="$onSurfaceIconPrimary"
        />
        <ParagraphText
          emphasis="subtle"
          size="xs"
          css={{
            textTransform: 'uppercase',
            fontWeight: 'bold',
            '&:not(:last-child)': { marginBlockEnd: 0 },
          }}
        >
          {label}
        </ParagraphText>
      </StackLayout>
      <StackLayout
        orientation="horizontal"
        spacing="$half"
        verticalAlignment="center"
        css={{ flexGrow: 2.5 }}
      >
        <Box css={{ flexGrow: 0, flexShrink: 0 }}>
          <NumberIcon number={targetNodeNumber ?? 0} />
        </Box>
        <StackLayout orientation="horizontal" spacing="$quarter">
          <Icon
            icon={itemNodeTypeIcon}
            size="$one"
            tint="$onSurfaceIconPrimary"
            css={{ marginBlockStart: '2px' }}
          />
          <ParagraphText emphasis="subtle" size="sm">
            {itemLabel}
          </ParagraphText>
        </StackLayout>
      </StackLayout>
    </StackLayout>
  );
};
interface TransitionListProps {
  transitions: Nullable<TransitionItemProps>[];
}
const TransitionList: React.FC<TransitionListProps> = ({ transitions }) => (
  <StackLayout
    as="ul"
    orientation="vertical"
    spacing="$half"
    horizontalAlignment="stretch"
  >
    {transitions?.map((transition, index) => {
      const key = `flow.transitions.${index}-${transition?.targetNode?.uniqueId}`;
      return (
        <TransitionItem
          key={key}
          transitionKey={transition?.targetNode?.uniqueId}
          targetNode={transition?.targetNode}
          targetNodeNumber={transition?.targetNodeNumber}
          label={transition?.label}
          itemLabel={transition?.itemLabel}
        />
      );
    })}
  </StackLayout>
);

export const FlowTransitions: React.FC<FlowTransitionsProps> = ({
  itemData,
  data,
  nodeNextKeyMap,
}) => {
  const { formatMessage } = useIntl();
  const nodeType = itemData?.nodeType;
  const nextKey = (() => {
    switch (nodeType) {
      case DECISION:
      case ACTION:
      case START:
        return nodeNextKeyMap[nodeType] || 'next';
      default:
        return 'next';
    }
  })();

  const getTargetNodeInfo = (
    nodeId: string,
  ): {
    targetNode: ComplexObjectArrayControlItem;
    targetNodeNumber: number;
  } | null => {
    const targetNodeIndex = data?.findIndex(
      (node: ComplexObjectArrayControlItem) =>
        node?.itemData?.nodeId === nodeId,
    );
    if (targetNodeIndex === undefined || targetNodeIndex < 0) {
      return null;
    }
    const targetNode = data[targetNodeIndex];

    return { targetNode, targetNodeNumber: targetNode.itemNumber };
  };

  // Decision nodes: expect array of DecisionNext at the derived next key
  if (nodeType === DECISION) {
    const nextVal = itemData[nextKey];
    if (!isDecisionNextArray(nextVal) || nextVal.length === 0) {
      return null;
    }

    const validTransitions = nextVal
      .map((nextItem) => {
        const nodeInfo = getTargetNodeInfo(nextItem?.nextNodeId);
        if (!nodeInfo) {
          return null;
        }

        const labelId =
          nextItem.result === 'true'
            ? DECISION_RESULT_LABELS.true
            : DECISION_RESULT_LABELS.false;
        const label = formatMessage({ id: labelId });

        return {
          ...nodeInfo,
          itemLabel: nodeInfo.targetNode.itemLabel,
          label,
          transitionKey: `${nextItem.nextNodeId}-${nextItem.result}`,
        };
      })
      .filter(Boolean);

    if (validTransitions.length === 0) {
      return null;
    }

    return <TransitionList transitions={validTransitions} />;
  }

  // Action/Start nodes: expect array of string node ids at the derived next key
  if (nodeType === ACTION || nodeType === START) {
    const nextVal = itemData[nextKey];
    if (!isActionNextArray(nextVal) || nextVal.length === 0) {
      return null;
    }

    const validTransitions = nextVal
      .map((transitionNodeId) => {
        const nodeInfo = getTargetNodeInfo(transitionNodeId);
        if (!nodeInfo) {
          return null;
        }

        const label = formatMessage({ id: 'NEXT' });

        return {
          ...nodeInfo,
          label,
          itemLabel: nodeInfo.targetNode.itemLabel,
          transitionKey: `${transitionNodeId}-next`,
        };
      })
      .filter(Boolean);

    if (validTransitions.length === 0) {
      return null;
    }

    return <TransitionList transitions={validTransitions} />;
  }

  return null;
};
